import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Check, Star, X } from 'lucide-react'
import WheelCanvas from './WheelCanvas'
import { NOTICE_AFTER_STOP_MS, SPIN_FALLBACK_MS, spinTargetRotation } from './wheelGeometry'
import { canCloseOnBackdrop } from './fabGeometry'
import { useT } from '../../hooks/useTranslation'
import { useDashboardStore } from '../../store/dashboardStore'
import {
  getFortune, createFortuneInvoice, createFortuneFreeSpin, getFortuneLast,
  markFortuneSpinStarted, markFortuneShown,
} from '../../services/api'

// Колесо фортуны — окно юзера.
//
// Внешний вид повторяет утверждённый прототип: небольшое окно по центру
// экрана (не нижний лист), заголовок, подпись колеса, три цены в звёздах,
// колесо с подписью «SPX» и белым указателем, цена прокрута, ссылка на
// историю, карточка приза и главная кнопка. История — отдельный экран
// внутри того же окна с кнопкой «Назад к колесу».
//
// Экономика: каждый платный прокрут кладёт долю (share × k × чистый курс) в
// личную копилку юзера на этом колесе и на этот приз. Сервер сам решает, какой
// приз выпал (см. TG-BOT/src/fortuneWheel.js), а фронт только доигрывает
// анимацию до нужного сектора — поэтому подкрутить результат из браузера нельзя.
//
// Денежный приз приходит возвратом ⭐ на баланс Telegram (refundStarPayment),
// подписка продлевается сразу. Никакого «разового платежа» и «подписка не
// оформляется» в окне нет — это решение владельца.

const TIER_ORDER = ['base', 'mid', 'adv']
const TIER_LABEL = { base: 'fortuneTierBase', mid: 'fortuneTierMid', adv: 'fortuneTierAdv' }
const HISTORY_ROWS = 8

// Админ проходит тот же путь, что юзер, включая окно оплаты Telegram. После
// закрытия окна — та же короткая пауза «обрабатываем», что у юзера после
// оплаты, и только потом стартует прокрутка.
const ADMIN_SPIN_DELAY_MS = 900

const fmtUsd = (v) => {
  const n = Number(v) || 0
  return n % 1 ? n.toFixed(2) : n.toFixed(0)
}

function parseDbDate(s) {
  const str = String(s || '')
  // SQLite CURRENT_TIMESTAMP — «YYYY-MM-DD HH:MM:SS» в UTC.
  if (/^\d{4}-\d\d-\d\d \d\d:\d\d:\d\d$/.test(str)) return new Date(`${str.replace(' ', 'T')}Z`)
  return new Date(str)
}

function fmtDay(s) {
  const d = parseDbDate(s)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' })
}

export default function FortuneWheelModal({ onClose }) {
  const t = useT()
  const setIsModalOpen = useDashboardStore(s => s.setIsModalOpen)

  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState('')
  const [tier, setTier] = useState('base')
  const [view, setView] = useState('wheel')

  // idle → opening (создаём счёт) → waiting (оплата в окне Telegram) →
  // spinning (выигрыш известен, колесо доигрывает) → result.
  const [phase, setPhase] = useState('idle')
  const [win, setWin] = useState(null)
  const [note, setNote] = useState('')

  const [rotation, setRotation] = useState(0)
  const rotationRef = useRef(0)

  const pollRef = useRef(null)
  const pollTimeoutRef = useRef(null)
  const spinTimerRef = useRef(null)
  // Пауза админа между закрытием окна оплаты и стартом прокрутки.
  const adminTimerRef = useRef(null)
  const settledRef = useRef(false)
  const closedHandlerRef = useRef(null)
  const knownIdsRef = useRef(new Set())
  const mountedAtRef = useRef(Date.now())
  // Показ результата и подтверждение серверу: уведомление в чат уходит только
  // после полной остановки колеса + паузы NOTICE_AFTER_STOP_MS.
  const winRef = useRef(null)
  const noticeTimersRef = useRef(new Map())
  const finishedRef = useRef(false)

  const tiers = useMemo(() => {
    const raw = data?.tiers || {}
    return TIER_ORDER.map(id => raw[id]).filter(x => x && x.enabled)
  }, [data])

  const currentTier = tiers.find(x => x.id === tier) || tiers[0] || null
  // Призы у каждого колеса СВОИ (решение владельца 2026-10-20): при смене
  // колеса меняются и сектора. data.prizes — запасной вариант для старого
  // сервера, который отдаёт только общий список.
  const prizes = currentTier?.prizes || data?.prizes || []
  const busy = phase === 'opening' || phase === 'waiting' || phase === 'spinning'

  // Подписи секторов пересобираются только при смене языка, чтобы canvas
  // не перерисовывался на каждый рендер окна.
  const canvasLabels = useMemo(() => ({
    day: t.fortuneCanvasDay,
    demo: t.fortuneCanvasDemo,
    real: t.fortuneCanvasReal,
  }), [t])

  const load = useCallback(async () => {
    try {
      const r = await getFortune()
      setData(r)
      setLoadError('')
      // Если выбранное колесо выключили из админки — переключаемся на первое.
      setTier((prev) => {
        const enabled = TIER_ORDER
          .map(id => r?.tiers?.[id])
          .filter(x => x && x.enabled)
          .map(x => x.id)
        return enabled.includes(prev) ? prev : (enabled[0] || 'base')
      })
    } catch {
      setLoadError(t.fortuneLoadError)
    } finally {
      setLoading(false)
    }
  }, [t])

  useEffect(() => { load() }, [load])

  // Окно ведёт себя как остальные модалки: прячет нижнее меню, блокирует
  // прокрутку фона и закрывается по Esc (кроме шага оплаты).
  const busyRef = useRef(busy)
  busyRef.current = busy

  useEffect(() => {
    setIsModalOpen(true)
    const root = document.getElementById('root')
    const prev = root?.style.overflow ?? ''
    if (root) root.style.overflow = 'hidden'

    const onKey = (e) => {
      if (e.key === 'Escape' && !busyRef.current) onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => {
      setIsModalOpen(false)
      window.removeEventListener('keydown', onKey)
      if (root) root.style.overflow = prev
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Чистим всё, что могло остаться от недокрученного сценария.
  const clearTimers = useCallback(() => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null }
    if (pollTimeoutRef.current) { clearTimeout(pollTimeoutRef.current); pollTimeoutRef.current = null }
    if (spinTimerRef.current) { clearTimeout(spinTimerRef.current); spinTimerRef.current = null }
    if (adminTimerRef.current) { clearTimeout(adminTimerRef.current); adminTimerRef.current = null }
    try {
      const tg = window.Telegram?.WebApp
      if (closedHandlerRef.current && tg && typeof tg.offEvent === 'function') {
        tg.offEvent('invoiceClosed', closedHandlerRef.current)
      }
    } catch { /* некритично */ }
    closedHandlerRef.current = null
  }, [])

  useEffect(() => clearTimers, [clearTimers])

  // Колесо полностью остановилось. Через 3 секунды подтверждаем серверу, что
  // приз показан, — только тогда уходит уведомление в чат. Таймер НЕ очищаем
  // при закрытии окна: результат уже увиден, подтверждение всё равно нужно.
  const scheduleNotice = useCallback(() => {
    const w = winRef.current
    if (!w || !w.id || noticeTimersRef.current.has(w.id)) return
    const id = w.id
    const timer = setTimeout(() => {
      noticeTimersRef.current.delete(id)
      markFortuneShown(id).catch(() => { /* сервер дошлёт страховкой */ })
    }, NOTICE_AFTER_STOP_MS)
    noticeTimersRef.current.set(id, timer)
  }, [])

  // Один и тот же путь для transitionend и для страховочного таймера: показ
  // результата + подтверждение через 3 секунды. Повторные вызовы игнорируем.
  const finishSpin = useCallback(() => {
    if (finishedRef.current) return
    finishedRef.current = true
    if (spinTimerRef.current) { clearTimeout(spinTimerRef.current); spinTimerRef.current = null }
    setPhase((p) => (p === 'spinning' ? 'result' : p))
    scheduleNotice()
  }, [scheduleNotice])

  // Приз известен → доворачиваем колесо так, чтобы его сектор встал под
  // указатель сверху. Геометрия — в wheelGeometry.js.
  const landOn = useCallback((w) => {
    // Сектора ИМЕННО того колеса, на котором крутили: списки у колёс разные.
    const list = (w?.tier && data?.tiers?.[w.tier]?.prizes) || prizes
    const n = Math.max(1, list.length)
    const found = list.findIndex(p => p.id === w.prizeId)
    const idx = found < 0 ? 0 : found
    const next = spinTargetRotation(idx, n, rotationRef.current)
    rotationRef.current = next
    finishedRef.current = false
    winRef.current = w
    setWin(w)
    setPhase('spinning')
    setRotation(next)
    // Сообщаем серверу момент старта: страховочное сообщение и серверный
    // минимум «не раньше остановки» считаются от него, а не от оплаты
    // (счёт в Telegram мог висеть открытым).
    markFortuneSpinStarted(w.id).catch(() => { /* страховка всё равно есть */ })
    // Страховка: transitionend может не прийти (reduced motion, фон вкладки).
    // Таймер длиннее анимации, поэтому результат не покажется раньше остановки.
    spinTimerRef.current = setTimeout(finishSpin, SPIN_FALLBACK_MS)
    load()
  }, [data, prizes, load, finishSpin])

  const startPolling = useCallback(() => {
    const check = async () => {
      try {
        const r = await getFortuneLast()
        const w = r?.win
        if (w && !knownIdsRef.current.has(w.id)) {
          clearTimers()
          landOn(w)
        }
      } catch { /* сеть моргнула — следующий тик */ }
    }
    clearTimers()
    pollRef.current = setInterval(check, 1500)
    // Бот финализирует платёж за секунды; 45 с — с большим запасом.
    pollTimeoutRef.current = setTimeout(() => {
      if (!pollRef.current) return
      clearTimers()
      setPhase('idle')
      setNote(t.fortuneLatePaid)
      load()
    }, 45000)
    // Первый тик сразу — часто приз уже записан ботом к закрытию окна оплаты.
    check()
  }, [clearTimers, landOn, load, t])

  const settle = useCallback((status) => {
    if (settledRef.current) return
    settledRef.current = true
    try {
      const tg = window.Telegram?.WebApp
      if (closedHandlerRef.current && tg && typeof tg.offEvent === 'function') {
        tg.offEvent('invoiceClosed', closedHandlerRef.current)
      }
    } catch { /* некритично */ }
    closedHandlerRef.current = null

    if (status === 'paid') {
      setPhase('waiting')
      startPolling()
      return
    }
    // cancelled | failed | pending — возвращаемся к выбору колеса.
    setPhase('idle')
  }, [startPolling])

  const spin = async ({ again = false } = {}) => {
    const allowed = phase === 'idle' || (again && phase === 'result')
    if (!allowed || !currentTier) return
    const tg = window.Telegram?.WebApp
    setNote('')
    setWin(null)
    winRef.current = null
    finishedRef.current = false
    setPhase('opening')
    settledRef.current = false
    knownIdsRef.current = new Set((data?.history || []).map(h => h.id))

    let invoice
    try {
      invoice = await createFortuneInvoice(currentTier.id)
    } catch (e) {
      setPhase('idle')
      setNote(e?.message || t.fortuneInvoiceError)
      return
    }
    if (!invoice?.ok) {
      setPhase('idle')
      setNote(t.fortuneInvoiceError)
      return
    }

    // Админ: путь РОВНО как у обычного юзера — включая окно оплаты Telegram с
    // настоящей суммой и кнопкой «Оплатить». Платить ему нечего: счёт помечен
    // как тестовый, оплату сервер не пропускает и звёзды не списывает. Когда
    // окно закрыли — прокрут всё равно начинается, и дальше всё как обычно:
    // анимация, приз, история, уведомление в чат.
    if (invoice.free) {
      const startFree = async () => {
        try {
          const r = await createFortuneFreeSpin(currentTier.id)
          if (!r?.ok || !r.win) throw new Error('')
          // Тот же путь, что после оплаты: колесо доворачивается на выпавший
          // сектор, сообщение в чат уходит после остановки.
          landOn(r.win)
        } catch (e) {
          setPhase('idle')
          setNote(e?.message || t.fortuneFreeError)
        }
      }

      const openSettle = (status) => {
        if (status === 'paid') {
          // Оплата не должна пройти (сервер её отклоняет), но если Telegram
          // всё же прислал «paid» — идём обычным путём оплаты, без двойного приза.
          settle(status)
          return
        }
        if (settledRef.current) return
        settledRef.current = true
        // Как у юзера после оплаты: короткая пауза «обрабатываем», затем прокрут.
        setPhase('waiting')
        adminTimerRef.current = setTimeout(() => {
          adminTimerRef.current = null
          startFree()
        }, ADMIN_SPIN_DELAY_MS)
      }

      const tgFree = window.Telegram?.WebApp
      if (!tgFree || typeof tgFree.openInvoice !== 'function' || !invoice.invoiceLink) {
        // Вне Telegram окна оплаты нет — крутим сразу.
        openSettle('cancelled')
        return
      }

      closedHandlerRef.current = openSettle
      try {
        if (typeof tgFree.onEvent === 'function') tgFree.onEvent('invoiceClosed', openSettle)
      } catch { /* обработает колбэк/промис */ }
      try {
        const maybe = tgFree.openInvoice(invoice.invoiceLink, (status) => openSettle(status))
        if (maybe && typeof maybe.then === 'function') {
          maybe.then(s => openSettle(s)).catch(() => openSettle('failed'))
        }
      } catch {
        openSettle('failed')
      }
      return
    }

    if (!tg || typeof tg.openInvoice !== 'function') {
      setPhase('idle')
      setNote(t.fortuneNotInTelegram)
      return
    }
    if (!invoice.invoiceLink) {
      setPhase('idle')
      setNote(t.fortuneInvoiceError)
      return
    }

    const onClosed = (e) => settle(e && e.status)
    closedHandlerRef.current = onClosed
    try {
      if (typeof tg.onEvent === 'function') tg.onEvent('invoiceClosed', onClosed)
    } catch { /* обработает колбэк/промис */ }

    try {
      const maybe = tg.openInvoice(invoice.invoiceLink, (status) => settle(status))
      if (maybe && typeof maybe.then === 'function') {
        maybe.then(s => settle(s)).catch(() => settle('failed'))
      }
    } catch {
      settle('failed')
    }
  }

  const close = () => onClose()

  // Закрытие по подложке. Проверяем, что нажали именно мимо окна, и не
  // реагируем на «хвостовой» клик того же тапа, которым открыли окно:
  // иначе окно закрывалось бы сразу после открытия — как будто кнопка
  // подарка не сработала.
  const onOverlayClick = (e) => {
    if (canCloseOnBackdrop({
      busy,
      sameTarget: e.target === e.currentTarget,
      elapsedMs: Date.now() - mountedAtRef.current,
    })) close()
  }

  const tierName = (id) => t[TIER_LABEL[id]] || id

  const prizeDaysText = (p) => (
    p.kind === 'real'
      ? t.fortunePrizeDaysReal.replace('{days}', p.days)
      : t.fortunePrizeDaysDemo.replace('{days}', p.days)
  )

  const winTitle = (w) => (
    w.kind === 'money'
      ? `+$${fmtUsd(w.usd)}`
      : prizeDaysText(w)
  )

  const winWhere = (w) => {
    if (w.kind === 'money') {
      const stars = w.stars || Math.round((Number(w.usd) || 0) / (Number(data?.rate?.buyerUsdPerStar) || 0.0199))
      return t.fortunePrizeWhereMoney
        .replace('{usd}', fmtUsd(w.usd))
        .replace('{stars}', stars)
    }
    return t.fortunePrizeWhereDays
      .replace('{account}', w.kind === 'real' ? t.fortuneAccountReal : t.fortuneAccountDemo)
      .replace('{days}', w.days)
  }

  const history = data?.history || []
  const starsPerUsd = Math.max(1, Math.round(1 / (Number(data?.rate?.buyerUsdPerStar) || 0.0199)))

  const statusText = (h) => {
    if (h.kind !== 'money') return t.fortuneHistoryDone
    return h.status === 'paid' ? t.fortuneHistoryPaid : t.fortuneHistoryPending
  }

  const buttonLabel = phase === 'opening'
    ? t.fortuneProcessing
    : phase === 'waiting'
      ? t.fortuneWaiting
      : phase === 'spinning'
        ? t.fortuneSpinning
        : phase === 'result'
          ? t.fortuneSpinAgainFor.replace('{stars}', currentTier?.stars ?? '—')
          : t.fortuneSpinFor.replace('{stars}', currentTier?.stars ?? '—')

  const disabled = busy || !currentTier || !data?.enabled

  const onMain = () => {
    if (phase === 'result') {
      setView('wheel')
      spin({ again: true })
      return
    }
    spin()
  }

  const wheelView = (
    <>
      <h3 className="fortune-window-title">{t.fortuneTitle}</h3>
      <p className="fortune-window-tier">
        {currentTier ? `${tierName(currentTier.id)} · ${currentTier.stars} ⭐` : ''}
      </p>

      <div className="fortune-pick-label">{t.fortunePickLabel}</div>
      <div className="fortune-picker">
        {tiers.map(x => {
          const on = x.id === currentTier?.id
          return (
            <button
              key={x.id}
              type="button"
              className={`fortune-pick${on ? ' is-on' : ''}`}
              onClick={() => { if (!busy) { setTier(x.id); setNote('') } }}
              disabled={busy}
            >
              <b>{tierName(x.id)}</b>
              <span>{x.stars} ⭐</span>
            </button>
          )
        })}
      </div>

      <div className="fortune-starnote">
        <Star size={14} strokeWidth={1.8} fill="currentColor" aria-hidden="true" />
        <span>
          <b>{t.fortuneStarNoteBold}</b>{t.fortuneStarNoteTail}
        </span>
      </div>

      <WheelCanvas
        prizes={prizes}
        rotation={rotation}
        spinning={phase === 'spinning'}
        onSpinEnd={finishSpin}
        labels={canvasLabels}
      />

      <p className="fortune-price">
        {t.fortunePriceLabel} — <b>{currentTier?.stars ?? '—'} ⭐ ≈ ${fmtUsd(currentTier?.usd)}</b>
      </p>
      <button type="button" className="fortune-hist-link" onClick={() => setView('history')}>
        {t.fortuneHistoryLink}
      </button>

      {phase === 'result' && win && (
        <div className="fortune-result">
          <div className="fortune-result-ico">
            <Check size={16} strokeWidth={2.2} />
          </div>
          <div>
            <div className="fortune-result-label">{t.fortuneYourPrize}</div>
            <div className="fortune-result-value">{winTitle(win)}</div>
            <div className="fortune-result-sub">{winWhere(win)}</div>
          </div>
        </div>
      )}

      <button type="button" className="fortune-main" onClick={onMain} disabled={disabled}>
        {buttonLabel}
      </button>

      {note && <div className="fortune-note">{note}</div>}

      <p className="fortune-legend">
        {t.fortuneLegendDays} {t.fortuneLegendMoney.replace('{stars}', starsPerUsd)}
      </p>
    </>
  )

  const historyView = (
    <>
      <h3 className="fortune-window-title">{t.fortuneHistoryLink}</h3>
      <p className="fortune-window-tier">
        {t.fortuneHistorySub.replace('{days}', data?.historyDays || 30)}
      </p>

      <div className="fortune-hist">
        {history.length === 0 ? (
          <div className="fortune-hist-empty">
            <b>{t.fortuneHistoryEmptyTitle}</b>
            <span>{t.fortuneHistoryEmptyText}</span>
          </div>
        ) : (
          history.slice(0, HISTORY_ROWS).map(h => {
            const waiting = h.kind === 'money' && h.status !== 'paid'
            return (
              <div key={h.id} className="fortune-hist-row">
                <span className={`fortune-hist-prize${h.kind === 'money' ? ' is-money' : ''}`}>
                  {h.kind === 'money'
                    ? `+$${fmtUsd(h.usd)}`
                    : `+${h.days} ${t.fortuneCanvasDay} ${h.kind === 'real' ? t.fortuneCanvasReal : t.fortuneCanvasDemo}`}
                </span>
                <span className="fortune-hist-date">
                  {fmtDay(h.createdAt)} · {(tierName(h.tier)).toLowerCase()}
                </span>
                <span className={`fortune-hist-status ${waiting ? 'is-wait' : 'is-ok'}`}>
                  {statusText(h)}
                </span>
              </div>
            )
          })
        )}
      </div>

      <button type="button" className="fortune-main" style={{ marginTop: 14 }} onClick={() => setView('wheel')}>
        {t.fortuneHistoryBack}
      </button>
    </>
  )

  return (
    <div
      className="fortune-overlay"
      onClick={onOverlayClick}
    >
      <div
        className="fortune-window"
        role="dialog"
        aria-modal="true"
        aria-label={t.fortuneTitle}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          className="fortune-window-x"
          onClick={close}
          disabled={busy}
          aria-label={t.fortuneClose}
        >
          <X size={13} strokeWidth={2.5} />
        </button>

        {loading ? (
          <div className="fortune-loading">{t.fortuneProcessing}</div>
        ) : loadError ? (
          <div className="fortune-error">{loadError}</div>
        ) : !data?.enabled || tiers.length === 0 ? (
          <div className="fortune-error">{t.fortuneDisabled}</div>
        ) : view === 'history' ? (
          historyView
        ) : (
          wheelView
        )}
      </div>
    </div>
  )
}
